import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/api.js";
import roboImg from "../assets/robo.png";
import logoImg from "../assets/sporless-logo.png";
import {
  FiEye,
  FiEyeOff,
  FiArrowLeft,
  FiMail,
  FiLock,
  FiKey,
} from "react-icons/fi";

function persistLoginSession(data, email) {
  localStorage.setItem("token", data.token);
  localStorage.setItem("role", data.role);
  localStorage.setItem("email", email);
  localStorage.setItem("allowUserAppOnWeb", String(Boolean(data.allowUserAppOnWeb)));
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOTP] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("login"); // 'login' | 'forget' | 'firstTime'
  const [particles, setParticles] = useState([]);

  const navigate = useNavigate();

  // Generate floating particles for background
  useEffect(() => {
    const newParticles = Array.from({ length: 20 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 5 + 2,
      speed: Math.random() * 0.5 + 0.1,
      opacity: Math.random() * 0.5 + 0.3,
      delay: Math.random() * 5,
    }));
    setParticles(newParticles);
  }, []);

  // Add this at the top of your component, before the return statement
useEffect(() => {
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  
  return () => {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };
}, []);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleValidateUser = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post("/validate-user", { email });

      if (response.data.isFirstTime) {
        setMode("firstTime");
        setStep(2);
        setMessage("OTP sent to your email for verification");
      } else {
        setMode("login");
        setStep(2);
      }
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          "Something went wrong during validation."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFirstTimeOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post("/verify-otp", { email, otp });
      setMessage("OTP verified! Please create your password.");
      setStep(3);
    } catch (error) {
      setMessage(error.response?.data?.message || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  const getRedirectPath = (role) => {
    return role === 'user' ? '/client/overview' : '/content';
  };

  const handleFirstTimePasswordReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post("/reset-password", { email, password });

      persistLoginSession(response.data, email);

      navigate(getRedirectPath(response.data.role));
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to create password.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post("/login", { email, password });

      persistLoginSession(response.data, email);

      navigate(getRedirectPath(response.data.role));
    } catch (error) {
      setMessage(
        error.response?.data?.message || "Login failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgetPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post("/forget-password", { email });
      setMessage("OTP sent to your email!");
      setStep(3);
    } catch (error) {
      setMessage(error.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post("/password-otp-verify", { email, otp });
      setMessage("OTP verified! Please reset your password.");
      setStep(4);
    } catch (error) {
      setMessage(error.response?.data?.message || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post("/forget-reset-password", {
        email,
        password,
        confirmPassword,
      });

      persistLoginSession(response.data, email);

      navigate(getRedirectPath(response.data.role));
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentFormTitle = () => {
    if (mode === "forget") {
      return step === 4 ? "Reset Password" : "Forgot Password";
    }
    if (mode === "firstTime") {
      return "Account Setup";
    }
    return "Welcome Back";
  };

  const getCurrentFormSubtitle = () => {
    if (mode === "forget") {
      return step === 4
        ? "Create a new password"
        : "Enter your email to receive a OTP";
    }
    if (mode === "firstTime") {
      return step === 3
        ? "Create your secure password"
        : step === 2
        ? "Verify your email"
        : "Let's get started";
    }
    return step === 2 ? "Enter your password" : "Sign in to your account";
  };

  return (
    <div style={styles.pageContainer}>
      <style>{`
        @keyframes fastFloat {
          0% { transform: translate(-10px, -5px) rotate(-3deg); }
          20% { transform: translate(12px, -15px) rotate(4deg); }
          40% { transform: translate(-8px, -20px) rotate(-5deg); }
          60% { transform: translate(15px, -10px) rotate(3deg); }
          80% { transform: translate(-12px, -5px) rotate(-4deg); }
          100% { transform: translate(10px, 0px) rotate(3deg); }
        }
        .hover-float:hover {
          animation: fastFloat 1.2s linear infinite;
          will-change: transform;
        }
        .hover-float {
          transition: transform 0.4s ease-out; /* Smooth enter/exit */
        }
      `}</style>
      <img src={logoImg} alt="Logo" style={styles.logoImage} />
      {particles.map((particle, index) => (
        <div
          key={index}
          style={{
            ...styles.particle,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity,
            animation: `float ${particle.speed}s ease-in-out ${particle.delay}s infinite alternate`,
          }}
        />
      ))}

      <div style={styles.container}>
        <div style={styles.illustrationContainer}>
          <img
            src={roboImg}
            alt="Security Illustration"
            style={styles.illustration}
            className="hover-float"
          />
          <div style={styles.illustrationOverlay}></div>
        </div>

        <div style={styles.formContainer}>
          <div style={styles.formHeader}>
            <h1 style={styles.logo}>SpotlessAI</h1>
            <h2 style={styles.title}>{getCurrentFormTitle()}</h2>
            <p style={styles.subtitle}>{getCurrentFormSubtitle()}</p>
          </div>

          {message && (
            <div style={styles.messageContainer}>
              <p style={styles.message}>{message}</p>
            </div>
          )}

          <form
            style={styles.form}
            onSubmit={
              mode === "forget"
                ? step === 4
                  ? handleResetPasswordSubmit
                  : step === 3
                  ? handleOTPVerify
                  : handleForgetPasswordSubmit
                : mode === "firstTime"
                ? step === 3
                  ? handleFirstTimePasswordReset
                  : step === 2
                  ? handleFirstTimeOTPVerify
                  : handleValidateUser
                : step === 2
                ? handleLogin
                : handleValidateUser
            }
          >
            {mode === "forget" ? (
              <>
                {step === 1 || step === 2 ? (
                  <>
                    <div style={styles.inputGroup}>
                      <FiMail style={styles.inputIcon} />
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={styles.input}
                      />
                    </div>
                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Send OTP"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      style={styles.linkButton}
                    >
                      <FiArrowLeft style={{ marginRight: 8 }} />
                      Back to Login
                    </button>
                  </>
                ) : step === 3 ? (
                  <>
                    <p style={styles.emailNote}>
                      OTP sent to: <strong>{email}</strong>
                    </p>
                    <div style={styles.inputGroup}>
                      <FiKey style={styles.inputIcon} />
                      <input
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOTP(e.target.value)}
                        required
                        style={styles.input}
                        maxLength={6}
                      />
                    </div>
                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Verifying..." : "Verify OTP"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStep(2);
                        setOTP("");
                      }}
                      style={styles.linkButton}
                    >
                      Didn't receive code? Resend
                    </button>
                  </>
                ) : step === 4 ? (
                  <>
                    <div style={styles.inputGroup}>
                      <FiLock style={styles.inputIcon} />
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="New password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={styles.input}
                      />
                      <span
                        onClick={togglePasswordVisibility}
                        style={styles.eyeIcon}
                      >
                        {showPassword ? <FiEye /> : <FiEyeOff />}
                      </span>
                    </div>

                    <div style={styles.inputGroup}>
                      <FiLock style={styles.inputIcon} />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        style={styles.input}
                      />
                      <span
                        onClick={toggleConfirmPasswordVisibility}
                        style={styles.eyeIcon}
                      >
                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      </span>
                    </div>

                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Password"}
                    </button>
                  </>
                ) : null}
              </>
            ) : mode === "firstTime" ? (
              <>
                {step === 1 ? (
                  <>
                    <div style={styles.inputGroup}>
                      <FiMail style={styles.inputIcon} />
                      <input
                        type="email"
                        placeholder="Your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={styles.input}
                      />
                    </div>
                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Checking..." : "Continue"}
                    </button>
                  </>
                ) : step === 2 ? (
                  <>
                    <p style={styles.emailNote}>
                      OTP sent to: <strong>{email}</strong>
                    </p>
                    <div style={styles.inputGroup}>
                      <FiKey style={styles.inputIcon} />
                      <input
                        type="text"
                        placeholder="Enter verification code"
                        value={otp}
                        onChange={(e) => setOTP(e.target.value)}
                        required
                        style={styles.input}
                        maxLength={6}
                      />
                    </div>
                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Verifying..." : "Verify Account"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStep(1);
                        setOTP("");
                      }}
                      style={styles.linkButton}
                    >
                      Resend verification code
                    </button>
                  </>
                ) : step === 3 ? (
                  <>
                    <div style={styles.inputGroup}>
                      <FiLock style={styles.inputIcon} />
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Create password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={styles.input}
                      />
                      <span
                        onClick={togglePasswordVisibility}
                        style={styles.eyeIcon}
                      >
                        {showPassword ? <FiEye /> : <FiEyeOff />}
                      </span>
                    </div>

                    <div style={styles.inputGroup}>
                      <FiLock style={styles.inputIcon} />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        style={styles.input}
                      />
                      <span
                        onClick={toggleConfirmPasswordVisibility}
                        style={styles.eyeIcon}
                      >
                        {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
                      </span>
                    </div>

                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Setting up..." : "Complete Setup"}
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                {step === 1 && (
                  <>
                    <div style={styles.inputGroup}>
                      <FiMail style={styles.inputIcon} />
                      <input
                        type="email"
                        placeholder="Your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={styles.input}
                      />
                    </div>
                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Checking..." : "Continue"}
                    </button>
                  </>
                )}

                {step === 2 && (
                  <>
                    <p style={styles.emailNote}>
                      Signed in as: <strong>{email}</strong>
                    </p>
                    <div style={styles.inputGroup}>
                      <FiLock style={styles.inputIcon} />
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={styles.input}
                      />
                      <span
                        onClick={togglePasswordVisibility}
                        style={styles.eyeIcon}
                      >
                        {showPassword ? <FiEye /> : <FiEyeOff />}
                      </span>
                    </div>

                    <div style={styles.forgotPasswordContainer}>
                      <button
                        type="button"
                        onClick={() => {
                          setMode("forget");
                          setStep(1);
                          setPassword("");
                        }}
                        style={styles.linkButton}
                      >
                        Forgot password?
                      </button>
                    </div>

                    <button
                      type="submit"
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </button>
                  </>
                )}
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
const styles = {
  pageContainer: {
    minHeight: "100vh",
    backgroundColor: "#0f0a1a",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'Inter', sans-serif",
  },
  logoImage: {
    position: "absolute",
    top: "20px",
    right: "20px",
    height: "40px",
    zIndex: 10,
  },
  particle: {
    position: "absolute",
    backgroundColor: "rgba(138, 43, 226, 0.6)",
    borderRadius: "50%",
    zIndex: 0,
  },
  container: {
    display: "flex",
    minHeight: "100vh",
    position: "relative",
    zIndex: 1,
  },
  illustrationContainer: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg, #1a0a2e 0%, #0f0a1a 100%)",
    position: "relative",
    overflow: "hidden",
  },
  illustrationOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "radial-gradient(circle at center, rgba(138, 43, 226, 0.1) 0%, transparent 70%)",
  },
  illustration: {
    width: "70%",
    maxWidth: "500px",
    zIndex: 1,
    filter: "drop-shadow(0 0 20px rgba(138, 43, 226, 0.5))",
    transformOrigin: "center center",
  },
  formContainer: {
    width: "450px",
    padding: "60px 40px",
    backgroundColor: "rgba(15, 10, 26, 0.8)",
    backdropFilter: "blur(10px)",
    borderLeft: "1px solid rgba(138, 43, 226, 0.2)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "auto", 
    maxHeight: "100vh"
  },
  formHeader: {
    marginBottom: "30px",
    textAlign: "center",
  },
  logo: {
    color: "#fff",
    fontSize: "28px",
    fontWeight: "700",
    marginBottom: "10px",
    background: "linear-gradient(90deg, #8a2be2, #da70d6)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  title: {
    color: "#fff",
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "8px",
  },
  subtitle: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "14px",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  inputGroup: {
    position: "relative",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: "15px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "18px",
  },
  input: {
    width: "100%",
    padding: "14px 45px 14px 45px", // Right padding to make space for eye icon (45px)
    borderRadius: "8px",
    border: "1px solid rgba(138, 43, 226, 0.3)",
    backgroundColor: "rgba(138, 43, 226, 0.1)",
    color: "#fff",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box", // ensure padding included in width
  },
  inputFocus: {
    borderColor: "rgba(138, 43, 226, 0.7)",
    boxShadow: "0 0 0 2px rgba(138, 43, 226, 0.2)",
  },
  eyeIcon: {
    position: "absolute",
    right: "15px", // reduced from 40px to 15px for tighter positioning
    top: "50%",
    transform: "translateY(-50%)",
    color: "rgba(255, 255, 255, 0.5)",
    cursor: "pointer",
    fontSize: "18px",
    transition: "all 0.2s ease",
    userSelect: "none",
  },
  primaryButton: {
    padding: "14px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#8a2be2",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    marginTop: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  primaryButtonHover: {
    backgroundColor: "#9b4de3",
    boxShadow: "0 4px 12px rgba(138, 43, 226, 0.3)",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "13px",
    marginTop: "15px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  linkButtonHover: {
    color: "#8a2be2",
  },
  messageContainer: {
    padding: "12px",
    borderRadius: "6px",
    backgroundColor: "rgba(138, 43, 226, 0.2)",
    marginBottom: "20px",
    textAlign: "center",
  },
  message: {
    color: "#fff",
    fontSize: "13px",
    margin: 0,
  },
  emailNote: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "13px",
    marginBottom: "20px",
    textAlign: "center",
  },
  forgotPasswordContainer: {
    textAlign: "right",
    margin: "-10px 0 10px",
  },
  "@keyframes float": {
    "0%": {
      transform: "translateY(0)",
    },
    "100%": {
      transform: "translateY(-20px)",
    },
  },
};

